import { v4 as uuidv4 } from 'uuid';
import {
  CapitalStructureDto,
  CaseDto,
  EquityRefinancingEventSetDto,
} from '@app/shared/models/contracts/project-dto';

export const useCaseEventManagement = (capitalStructure: CapitalStructureDto) => {
  const getArrayIntersection = (arrays: string[][]) => {
    if (!arrays.length) {
      return [];
    }

    return arrays.reduce(
      (common, current) => common.filter((id) => current.includes(id)),
      arrays[0]
    );
  };

  const getEventsSharedAcrossCases = (
    eventSets: Record<string, EquityRefinancingEventSetDto>
  ): string[] => {
    const eventLists = Object.values(eventSets)
      .map((eventSet) => eventSet?.events ?? [])
      .filter((events) => events.length > 0);

    if (!eventLists.length) {
      return [];
    }

    return getArrayIntersection(eventLists);
  };

  const findUnassignedEventSets = (
    eventSets: Record<string, EquityRefinancingEventSetDto>,
    cases: CaseDto[]
  ): string[] => {
    const allEventSetIds = Object.keys(eventSets);
    const assignedEventSetIds = new Set(
      cases.map((c) => c.eventSetId).filter((id): id is string => id !== null)
    );

    return allEventSetIds.filter((id) => !assignedEventSetIds.has(id));
  };

  const createEventSetForNewCase = (
    cases: CaseDto[],
    sourceCase: CaseDto
  ): { eventSets: Record<string, EquityRefinancingEventSetDto>; eventSetId: string | null } => {
    const unassignedEventSets = findUnassignedEventSets(capitalStructure.eventSets, cases);

    // remove event sets that are not assigned to any case
    const eventSets = Object.fromEntries(
      Object.entries(capitalStructure.eventSets).filter(([id]) => !unassignedEventSets.includes(id))
    );

    // If <= 1 case, reuse the sourceCase event set
    if (cases.length <= 1) {
      return { eventSets, eventSetId: sourceCase.eventSetId! };
    }

    if (cases.some((c) => !c.eventSetId)) {
      return { eventSets, eventSetId: null };
    }

    const commonEvents = getEventsSharedAcrossCases(eventSets);
    const lastCaseEventSet = eventSets[sourceCase.eventSetId!];
    const lastCaseEvents = lastCaseEventSet?.events || [];

    const canReuseEventSet =
      lastCaseEvents.length === commonEvents.length &&
      lastCaseEvents.every((eventId) => commonEvents.includes(eventId));

    if (canReuseEventSet) {
      return {
        eventSets,
        eventSetId: sourceCase.eventSetId!,
      };
    }

    const existingMatchingEventSetId = Object.keys(eventSets).find((id) => {
      const eventSet = eventSets[id];
      const events = eventSet?.events || [];
      return (
        events.length === commonEvents.length &&
        events.every((eventId) => commonEvents.includes(eventId))
      );
    });

    if (existingMatchingEventSetId) {
      return {
        eventSets,
        eventSetId: existingMatchingEventSetId,
      };
    }

    // Create new event set with only common events
    const newEventSetId = uuidv4();

    return {
      eventSets: {
        ...eventSets,
        [newEventSetId]: { events: commonEvents },
      },
      eventSetId: newEventSetId,
    };
  };

  return {
    createEventSetForNewCase,
  };
};
